import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AdminApprovals, AdminUsers, AdminListings, AdminForum, AdminSocial,
  AdminSponsorships, AdminPayouts, AdminPoints, AdminClubs, AdminShipping, AdminPriceCompare, AdminCatalog,
  AdminAnnouncements, AdminSimHubTracks, AdminSimHubAnalytics, AdminSimHubClubSuggestions,
  AdminForumSpotlight, AdminSponsors, AdminStaff, AdminLegal, AdminContact, AdminModeration,
} from "@/components/admin/AdminSections";
import AdminNews from "@/components/admin/AdminNews";
import {
  LayoutDashboard, UserCheck, Users, Store, MessagesSquare, Image,
  Handshake, Banknote, Coins, Trophy, Truck, Search, BookOpen, Megaphone, Gauge, BarChart3, Lightbulb, Sparkles, Flag, Newspaper, UserCog, FileText, Mail, ShieldAlert,
} from "lucide-react";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "approvals", label: "Approvals", icon: UserCheck },
  { id: "users", label: "Users", icon: Users },
  { id: "moderation", label: "Moderation", icon: ShieldAlert },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "news", label: "News & Hot Content", icon: Newspaper },
  { id: "catalog", label: "Product Catalog", icon: BookOpen },
  { id: "listings", label: "Listings", icon: Store },
  { id: "forum", label: "Forum", icon: MessagesSquare },
  { id: "forumspotlight", label: "Forum Spotlight", icon: Sparkles },
  { id: "social", label: "Social", icon: Image },
  { id: "sponsorships", label: "Sponsorships", icon: Handshake },
  { id: "sponsors", label: "Sponsor Logos", icon: Flag },
  { id: "payouts", label: "Payouts", icon: Banknote },
  { id: "points", label: "Points", icon: Coins },
  { id: "clubs", label: "Clubs", icon: Trophy },
  { id: "clubsuggestions", label: "Club Suggestions", icon: Lightbulb },
  { id: "shipping", label: "Shipping", icon: Truck },
  { id: "legal", label: "Legal Pages", icon: FileText },
  { id: "contact", label: "Contact Inbox", icon: Mail },
  { id: "pricecompare", label: "Price Compare", icon: Search },
  { id: "simhubtracks", label: "SimHub Tracks", icon: Gauge },
  { id: "simhubanalytics", label: "SimHub Analytics", icon: BarChart3 },
  { id: "staff", label: "Staff Access", icon: UserCog, ownerOnly: true },
];

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { adminAPI.getStats().then((r) => setStats(r.data)).catch(() => setStats({})); }, []);
  if (!stats) return <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>;
  const counts = stats.counts || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Object.entries(counts).map(([k, v]) => (
          <Card key={k} className="border-border bg-card p-4" data-testid={`admin-stat-${k}`}>
            <p className="font-display text-2xl font-bold text-primary">{v}</p>
            <p className="text-xs capitalize text-muted-foreground">{k.replace(/_/g, " ")}</p>
          </Card>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border bg-card p-4">
          <h3 className="mb-3 font-semibold">Recent users</h3>
          {(stats.recent_users || []).map((u, i) => (
            <div key={i} className="flex justify-between border-t border-border py-2 text-sm first:border-0"><span>{u.username}</span><span className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span></div>
          ))}
        </Card>
        <Card className="border-border bg-card p-4">
          <h3 className="mb-3 font-semibold">Recent listings</h3>
          {(stats.recent_listings || []).map((l, i) => (
            <div key={i} className="flex justify-between border-t border-border py-2 text-sm first:border-0"><span className="truncate">{l.title}</span><span className="text-xs text-muted-foreground">${l.price}</span></div>
          ))}
        </Card>
      </div>
    </div>
  );
}

const RENDER = {
  overview: Overview,
  approvals: AdminApprovals,
  users: AdminUsers,
  announcements: AdminAnnouncements,
  news: AdminNews,
  catalog: AdminCatalog,
  listings: AdminListings,
  forum: AdminForum,
  forumspotlight: AdminForumSpotlight,
  social: AdminSocial,
  sponsorships: AdminSponsorships,
  sponsors: AdminSponsors,
  payouts: AdminPayouts,
  points: AdminPoints,
  clubs: AdminClubs,
  clubsuggestions: AdminSimHubClubSuggestions,
  shipping: AdminShipping,
  legal: AdminLegal,
  contact: AdminContact,
  pricecompare: AdminPriceCompare,
  simhubtracks: AdminSimHubTracks,
  simhubanalytics: AdminSimHubAnalytics,
  staff: AdminStaff,
  moderation: AdminModeration,
};

export default function Admin() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [perms, setPerms] = useState(null); // { is_owner, allowed_tabs }
  const [active, setActive] = useState(() => {
    const t = params.get("tab");
    return t && RENDER[t] ? t : "overview";
  });

  useEffect(() => {
    adminAPI.getMyPermissions()
      .then((r) => setPerms({ is_owner: !!r.data.is_owner, allowed_tabs: r.data.allowed_tabs }))
      .catch(() => setPerms({ is_owner: false, allowed_tabs: null }));
  }, []);

  const canAccess = (id) => {
    if (id === "overview") return true;
    if (!perms) return true; // optimistic until loaded
    if (id === "staff") return perms.is_owner;
    if (perms.allowed_tabs === null) return true; // full access
    return perms.allowed_tabs.includes(id);
  };

  const visibleSections = SECTIONS.filter((s) => canAccess(s.id));

  useEffect(() => {
    const t = params.get("tab");
    if (t && RENDER[t] && t !== active) setActive(t);
  }, [params, active]);

  // If the active tab isn't permitted (e.g. restricted staff deep-linked), fall back.
  useEffect(() => {
    if (perms && !canAccess(active)) {
      setActive("overview");
      const next = new URLSearchParams(params);
      next.delete("tab");
      setParams(next, { replace: true });
    }
  }, [perms]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTab = (id) => {
    setActive(id);
    const next = new URLSearchParams(params);
    if (id === "overview") next.delete("tab");
    else next.set("tab", id);
    setParams(next, { replace: true });
  };

  if (!user?.is_admin && !user?.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  const Section = canAccess(active) ? RENDER[active] : Overview;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground">Manage the Sim Racing Connected community</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <nav className="flex gap-2 overflow-x-auto lg:flex-col lg:gap-1">
          {visibleSections.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => selectTab(s.id)}
                data-testid={`admin-nav-${s.id}`}
                className={`flex flex-shrink-0 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${active === s.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"}`}
              >
                <Icon className="h-4 w-4" /> {s.label}
              </button>
            );
          })}
        </nav>
        <div className="min-w-0"><Section /></div>
      </div>
    </div>
  );
}
